import { eModules } from '@/constants'
import i18n from '@/lang/'
import { useUserStore } from '@/stores/user'
import { isTokenExpired } from '@/utils/tokenExpiry'

import { router } from './router'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const t = (key: string): string => (i18n.global as any).t(key)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const te = (key: string): boolean => (i18n.global as any).te(key)

import settings from './settings'

const whiteList = ['/login', '/auth-redirect']

const getPageTitle = (key: string) => {
  const hasKey = te(`route.${key}`)
  if (hasKey) {
    const pageName = t(`route.${key}`)
    return `${pageName} - ${settings.title}`
  }
  return `${settings.title}`
}

router.beforeEach(async (to, _, next) => {
  const userStore = useUserStore()
  const token = userStore.token
  const refreshToken = userStore.refreshToken

  // Determine whether the user has logged in
  if (!token) {
    // Has no token
    if (whiteList.includes(to.path)) {
      // In the free login whitelist, go directly
      next()
    } else {
      // Other pages that do not have permission to access are redirected to the login page.
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      })
    }
    return
  }

  // Check if tokens are expired
  const accessTokenExpired = isTokenExpired(token)
  const refreshTokenExpired = isTokenExpired(refreshToken)

  // If both tokens are expired, redirect to login
  if (accessTokenExpired && refreshTokenExpired) {
    await userStore.resetToken()
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      })
    }
    return
  }

  // If access token is expired but refresh token is valid, try to refresh
  if (accessTokenExpired && !refreshTokenExpired) {
    try {
      await userStore.refreshTokenAction(refreshToken)
    } catch {
      // Refresh failed, redirect to login
      await userStore.resetToken()
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      })
      return
    }
  }

  if (to.path === '/login') {
    // If is logged in, redirect to the home page
    next({ path: '/' })
    return
  }

  await userStore.setUserInfoFromToken()

  if (!to.meta?.allowedModules) {
    next()
    return
  }

  if (!userStore.allowedModules) {
    next({ path: '/' })
    return
  }

  const hasPermission = userStore.allowedModules.some((module: eModules) => {
    return to.meta?.allowedModules?.includes(module)
  })

  if (hasPermission) {
    next()
    return
  }

  // Didn't find required permission so redirect to home page
  next({ path: '/' })
})

router.afterEach(to => {
  // set page title
  document.title = getPageTitle((to.meta?.title as string) ?? '')
})
